// src/components/ScribbleText.jsx
// Renders text with a hand-drawn scribble strikethrough,
// like crossing out a word in a notebook with a pen.
import React, { useMemo } from 'react';

// Generate a wobbly SVG path that looks hand-drawn
const generateScribblePath = (seed) => {
  // Use a simple seeded pseudo-random so the scribble is stable per-text
  let s = seed;
  const rand = () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s % 1000) / 1000;
  };

  const points = 8;
  const segments = [];

  // Main scribble pass (left to right)
  for (let i = 0; i <= points; i++) {
    const x = (i / points) * 100;
    const y = 50 + (rand() - 0.5) * 60;
    if (i === 0) {
      segments.push(`M ${x} ${y}`);
    } else {
      const cpx = ((i - 0.5) / points) * 100;
      const cpy = 50 + (rand() - 0.5) * 50;
      segments.push(`Q ${cpx} ${cpy} ${x} ${y}`);
    }
  }

  // Sometimes add a second overlapping pass for extra messiness
  if (rand() > 0.3) {
    for (let i = points; i >= 0; i--) {
      const x = (i / points) * 100;
      const y = 50 + (rand() - 0.5) * 40;
      const cpx = ((i + 0.5) / points) * 100;
      const cpy = 50 + (rand() - 0.5) * 45;
      segments.push(`Q ${cpx} ${cpy} ${x} ${y}`);
    }
  }

  return segments.join(' ');
};

// Hash a string to a number for seeding
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) || 1;
};

const ScribbleText = ({ children, className = '' }) => {
  const text = typeof children === 'string' ? children : '';
  const path = useMemo(() => generateScribblePath(hashString(text)), [text]);

  return (
    <span className={`scribble-through ${className}`}>
      {children}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={path} />
      </svg>
    </span>
  );
};

export default ScribbleText;
